import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Button,
  TextField,
  Tooltip,
  IconButton,
  Container,
  Card,
  CardContent,
  Chip,
  TableContainer,
  alpha,
  useTheme,
  Fade,
  InputAdornment,
  Stack,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  RestartAlt as RestartAltIcon,
  PersonOff as PersonOffIcon,
  Label as LabelIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TouchApp as TouchAppIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { AuthContext } from '../contexts/AuthContext';

const ManageTags = () => {
  const { currentUser } = useContext(AuthContext);
  const theme = useTheme();
  const [userClientIds, setUserClientIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');

  const [placements, setPlacements] = useState(['Person', 'Stall/DT']);
  const [selectedPlacement, setSelectedPlacement] = useState('');

  const [assignStatusOptions] = useState(['Assigned', 'Unassigned']);
  const [selectedAssignStatus, setSelectedAssignStatus] = useState('Assigned');

  const [tags, setTags] = useState([]);

  const [loadingUserClients, setLoadingUserClients] = useState(true);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);

  const [errorMsg, setErrorMsg] = useState('');
  const [unassignWarning, setUnassignWarning] = useState('');

  const [editedAssignedTos, setEditedAssignedTos] = useState({});
  const [editingRows, setEditingRows] = useState({});

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, tag: null });

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) {
        setErrorMsg('User not authenticated.');
        return;
      }

      setLoadingUserClients(true);
      setErrorMsg('');
      setUnassignWarning('');
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (!userSnap.exists()) {
          setErrorMsg('User data not found.');
          return;
        }

        const userData = userSnap.data();
        const cIds = userData.clientIds || [];
        setUserClientIds(cIds);
      } catch (error) {
        console.error('Error fetching user clients:', error);
        setErrorMsg('Failed to load user clients. Please try again.');
      } finally {
        setLoadingUserClients(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  useEffect(() => {
    const fetchClients = async () => {
      if (userClientIds.length === 0) {
        setClients([]);
        return;
      }

      setLoadingClients(true);
      setErrorMsg('');
      setUnassignWarning('');

      try {
        const clientsRef = collection(db, 'clients');
        const allClientsData = [];
        const clientIdsChunks = [];

        for (let i = 0; i < userClientIds.length; i += 30) {
          clientIdsChunks.push(userClientIds.slice(i, i + 30));
        }

        const promises = clientIdsChunks.map(chunk => {
          const q = query(clientsRef, where('__name__', 'in', chunk));
          return getDocs(q);
        });

        const querySnapshots = await Promise.all(promises);

        querySnapshots.forEach(snapshot => {
          snapshot.forEach(docSnap => {
            allClientsData.push({ id: docSnap.id, ...docSnap.data() });
          });
        });

        setClients(allClientsData);
      } catch (error) {
        console.error('Error fetching assigned clients:', error);
        setErrorMsg('Failed to load assigned clients. Please try again.');
      } finally {
        setLoadingClients(false);
      }
    };

    if (!loadingUserClients && userClientIds.length > 0) {
      fetchClients();
    }
  }, [loadingUserClients, userClientIds]);

  const fetchTags = async () => {
    if (!selectedClientId || !selectedPlacement) {
      setTags([]);
      return;
    }

    setLoadingTags(true);
    setErrorMsg('');
    setUnassignWarning('');

    try {
      const tagsRef = collection(db, 'clients', selectedClientId, 'nfcTags');
      const tagsQ = query(tagsRef, where('placement', '==', selectedPlacement));
      const tagsSnap = await getDocs(tagsQ);

      const tagsData = [];
      tagsSnap.forEach(docSnap => {
        const data = docSnap.data();
        tagsData.push(data);
      });

      let filteredTags = tagsData;

      if (selectedAssignStatus === 'Assigned') {
        filteredTags = tagsData.filter(tag => tag.assignedTo !== 'Unassigned');
      } else if (selectedAssignStatus === 'Unassigned') {
        filteredTags = tagsData.filter(tag => tag.assignedTo === 'Unassigned');
      }

      setTags(filteredTags);

      const initialEdits = {};
      const initialEditing = {};
      filteredTags.forEach(tag => {
        const tagKey = tag.tagId || tag.id;
        initialEdits[tagKey] = tag.assignedTo || '';
        initialEditing[tagKey] = false;
      });
      setEditedAssignedTos(initialEdits);
      setEditingRows(initialEditing);

    } catch (error) {
      console.error('Error fetching tags:', error);
      setErrorMsg('Failed to load tags. Please try again.');
    } finally {
      setLoadingTags(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, [selectedClientId, selectedPlacement, selectedAssignStatus]);

  const handleRequestId = async (tag) => {
    if (!selectedClientId || !tag.tagId) return;

    try {
      const tagDocRef = doc(db, 'clients', selectedClientId, 'nfcTags', tag.tagId);
      await updateDoc(tagDocRef, { id: tag.tagId });
      await fetchTags();
    } catch (error) {
      console.error('Error requesting ID:', error);
      setErrorMsg('Failed to request ID. Please try again.');
    }
  };

  const handleSaveAssignedTo = async (tag) => {
    const tagKey = tag.tagId || tag.id;
    const newAssignedTo = editedAssignedTos[tagKey];

    if (!selectedClientId || !tagKey || !newAssignedTo) return;

    if (newAssignedTo.trim().toLowerCase() === 'unassigned') {
      setUnassignWarning("You cannot manually set 'Assigned To' to Unassigned. Please use the Unassign action button.");
      const originalTag = tags.find(t => (t.tagId || t.id) === tagKey);
      const originalAssignedTo = originalTag ? originalTag.assignedTo || '' : '';
      setEditedAssignedTos(prev => ({ ...prev, [tagKey]: originalAssignedTo }));
      setEditingRows(prev => ({ ...prev, [tagKey]: false }));
      return;
    }

    try {
      const tagDocRef = doc(db, 'clients', selectedClientId, 'nfcTags', tag.tagId || tag.id);
      await updateDoc(tagDocRef, { assignedTo: newAssignedTo });
      await fetchTags();
      setEditingRows(prev => ({ ...prev, [tagKey]: false }));
    } catch (error) {
      console.error('Error saving assignedTo:', error);
      setErrorMsg('Failed to save assignedTo. Please try again.');
    }
  };

  const handleEditClick = (tag) => {
    const tagKey = tag.tagId || tag.id;
    setEditingRows(prev => ({ ...prev, [tagKey]: true }));
  };

  const handleCancelEdit = (tag) => {
    const tagKey = tag.tagId || tag.id;
    const originalTag = tags.find(t => (t.tagId || t.id) === tagKey);
    const originalAssignedTo = originalTag ? originalTag.assignedTo || '' : '';
    setEditedAssignedTos(prev => ({ ...prev, [tagKey]: originalAssignedTo }));
    setEditingRows(prev => ({ ...prev, [tagKey]: false }));
  };

  const handleResetStats = async (tag) => {
    setConfirmDialog({ open: true, action: 'reset', tag });
  };

  const handleUnassign = async (tag) => {
    setConfirmDialog({ open: true, action: 'unassign', tag });
  };

  const handleConfirmAction = async () => {
    const { action, tag } = confirmDialog;
    if (!selectedClientId || (!tag.id && !tag.tagId)) return;

    try {
      const tagKey = tag.tagId || tag.id;
      const tagDocRef = doc(db, 'clients', selectedClientId, 'nfcTags', tagKey);

      if (action === 'reset') {
        await updateDoc(tagDocRef, { totalHits: 0, totalYes: 0, totalNo: 0 });
      } else if (action === 'unassign') {
        await updateDoc(tagDocRef, {
          assignedTo: 'Unassigned',
          totalHits: 0,
          totalYes: 0,
          totalNo: 0
        });
      }

      await fetchTags();
      setConfirmDialog({ open: false, action: null, tag: null });
    } catch (error) {
      console.error(`Error ${action}ing tag:`, error);
      setErrorMsg(`Failed to ${action} tag. Please try again.`);
    }
  };

  const formatLastHit = (timestamp) => {
    if (!timestamp) return { date: 'Never', time: '' };
    const date = timestamp.toDate();
    return {
      date: date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    };
  };

  // Calculate statistics
  const totalTagsCount = tags.length;
  const assignedCount = tags.filter(tag => 
    tag.assignedTo !== 'Unassigned' && 
    tag.assignedTo !== 'New Card'
  ).length;
  const totalHitsCount = tags.reduce((sum, tag) => sum + (tag.totalHits || 0), 0);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LabelIcon sx={{ 
            fontSize: 40, 
            mr: 2, 
            color: theme.palette.primary.main 
          }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.text.primary,
              fontFamily: 'Inter, Montserrat, sans-serif'
            }}
          >
            Manage Tags
          </Typography>
        </Box>
        <Typography 
          variant="body1" 
          sx={{ 
            color: theme.palette.text.secondary,
            mb: 3
          }}
        >
          Monitor and manage NFC tags across your clients and locations
        </Typography>
      </Box>

      {/* Alert Messages */}
      <Fade in={!!errorMsg}>
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          icon={<WarningIcon />}
          onClose={() => setErrorMsg('')}
        >
          {errorMsg}
        </Alert>
      </Fade>
      
      <Fade in={!!unassignWarning}>
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          onClose={() => setUnassignWarning('')}
        >
          {unassignWarning}
        </Alert>
      </Fade>

      {/* Selection Controls */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          background: alpha(theme.palette.primary.main, 0.02)
        }}
      >
        <Stack spacing={2}>
          {/* Client Selection */}
          <FormControl 
            fullWidth 
            disabled={loadingClients || userClientIds.length === 0}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                }
              }
            }}
          >
            <InputLabel id="client-select-label">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                Select Client
              </Box>
            </InputLabel>
            {loadingClients ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Select
                labelId="client-select-label"
                value={selectedClientId}
                label="Select Client"
                onChange={(e) => setSelectedClientId(e.target.value)}
                startAdornment={
                  selectedClientId && (
                    <InputAdornment position="start">
                      <BusinessIcon color="primary" />
                    </InputAdornment>
                  )
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {clients.map(client => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.displayName || client.id}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>

          {/* Placement and Status Selection */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl 
              fullWidth 
              disabled={!selectedClientId}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  }
                }
              }}
            >
              <InputLabel id="placement-select-label">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CategoryIcon sx={{ mr: 1, fontSize: 20 }} />
                  Select Placement
                </Box>
              </InputLabel>
              <Select
                labelId="placement-select-label"
                value={selectedPlacement}
                label="Select Placement"
                onChange={(e) => setSelectedPlacement(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {placements.map((placement) => (
                  <MenuItem key={placement} value={placement}>
                    {placement}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              fullWidth 
              disabled={!selectedPlacement}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  }
                }
              }}
            >
              <InputLabel id="assign-status-select-label">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssignmentIcon sx={{ mr: 1, fontSize: 20 }} />
                  Filter by Status
                </Box>
              </InputLabel>
              <Select
                labelId="assign-status-select-label"
                value={selectedAssignStatus}
                label="Filter by Status"
                onChange={(e) => setSelectedAssignStatus(e.target.value)}
              >
                <MenuItem value="Assigned">
                  <Chip 
                    label="Assigned" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </MenuItem>
                <MenuItem value="Unassigned">
                  <Chip 
                    label="Unassigned" 
                    size="small" 
                    color="default" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Paper>

      {/* Statistics Cards */}
      {selectedClientId && selectedPlacement && !loadingTags && (
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Card sx={{ 
            flex: 1,
            background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {totalTagsCount}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Total Tags
                  </Typography>
                </Box>
                <LabelIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            flex: 1,
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {assignedCount}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Assigned
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            flex: 1,
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {totalHitsCount}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Total Hits
                  </Typography>
                </Box>
                <TouchAppIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tags Table */}
      {loadingTags ? (
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={60} />
            ))}
          </Stack>
        </Paper>
      ) : selectedClientId && selectedPlacement && (
        tags.length === 0 ? (
          <Paper 
            elevation={0}
            sx={{ 
              p: 6, 
              textAlign: 'center',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <LabelIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
              No tags found
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              No tags match the selected filters
            </Typography>
          </Paper>
        ) : (
          <Paper 
            elevation={0}
            sx={{ 
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden'
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    '& th': {
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: theme.palette.text.secondary,
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      py: 2
                    }
                  }}>
                    <TableCell align="center">TAG ID</TableCell>
                    <TableCell align="center">ASSIGNED TO</TableCell>
                    <TableCell align="center">STATISTICS</TableCell>
                    <TableCell align="center">LAST HIT</TableCell>
                    <TableCell align="center">ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tags.map((tag, index) => {
                    const tagKey = tag.tagId || tag.id || index;
                    const currentAssignedTo = editedAssignedTos[tagKey] || '';
                    const totalHits = tag.totalHits || 0;
                    const totalYes = tag.totalYes || 0;
                    const totalNo = tag.totalNo || 0;
                    const isEditing = editingRows[tagKey];
                    const lastHit = formatLastHit(tag.lastHit);

                    return (
                      <TableRow 
                        key={tag.id || tag.tagId || index}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.02)
                          },
                          '& td': {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            py: 2
                          }
                        }}
                      >
                        <TableCell align="center">
                          {tag.id ? (
                            <Chip 
                              label={tag.id}
                              color="primary"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ) : (
                            <Button 
                              variant="outlined" 
                              size="small" 
                              onClick={() => handleRequestId(tag)}
                              startIcon={<LabelIcon />}
                              sx={{ textTransform: 'none' }}
                            >
                              Request ID
                            </Button>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {isEditing ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                              <TextField
                                variant="outlined"
                                size="small"
                                value={currentAssignedTo}
                                onChange={(e) =>
                                  setEditedAssignedTos(prev => ({ ...prev, [tagKey]: e.target.value }))
                                }
                                sx={{ 
                                  width: '200px',
                                  '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main,
                                    }
                                  }
                                }}
                                autoFocus
                              />
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleSaveAssignedTo(tag)}
                              >
                                <SaveIcon />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleCancelEdit(tag)}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Chip
                                label={currentAssignedTo || 'Unassigned'}
                                color={currentAssignedTo === 'Unassigned' ? 'default' : 'success'}
                                variant="filled"
                                sx={{ 
                                  minWidth: 120,
                                  '& .MuiChip-label': {
                                    px: 2
                                  }
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(tag)}
                                sx={{ 
                                  opacity: 0.7,
                                  '&:hover': { opacity: 1 }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                                {totalHits}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                Hits
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                                {totalYes}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                Yes
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                                {totalNo}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                No
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {lastHit.date === 'Never' ? (
                            <Chip 
                              icon={<ScheduleIcon />}
                              label="Never"
                              size="small"
                              variant="outlined"
                              sx={{ color: theme.palette.text.disabled }}
                            />
                          ) : (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {lastHit.date}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {lastHit.time}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="Reset Statistics">
                              <IconButton 
                                size="small" 
                                onClick={() => handleResetStats(tag)}
                                sx={{
                                  color: theme.palette.warning.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.warning.main, 0.1)
                                  }
                                }}
                              >
                                <RestartAltIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Unassign Tag">
                              <IconButton 
                                size="small" 
                                onClick={() => handleUnassign(tag)}
                                sx={{
                                  color: theme.palette.error.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, 0.1)
                                  }
                                }}
                              >
                                <PersonOffIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, tag: null })}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningIcon color="warning" />
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'reset' 
              ? 'Are you sure you want to reset the statistics for this tag? This action cannot be undone.'
              : 'Are you sure you want to unassign this tag and reset its statistics? This action cannot be undone.'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setConfirmDialog({ open: false, action: null, tag: null })}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAction} 
            variant="contained"
            color={confirmDialog.action === 'reset' ? 'warning' : 'error'}
            sx={{ textTransform: 'none' }}
          >
            {confirmDialog.action === 'reset' ? 'Reset Stats' : 'Unassign Tag'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageTags;